// File: src/components/home/ExploreByBodyType.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCity } from "../../context/CityContext";
import { searchCarsAdvanced } from "../../services/car";
import "../../styles/home/ExploreByBodyType.css";

/* ── Body type tabs — keys must match your backend body_type values ── */
const BODY_TYPES = [
  {
    key: "Hatchback",
    label: "Hatchback",
    icon: (
      <svg viewBox="0 0 80 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 28 L18 14 L50 14 L64 28" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round"/>
        <rect x="8" y="28" width="62" height="6" rx="1.5" stroke="currentColor" strokeWidth="2.2"/>
        <circle cx="20" cy="34" r="4.5" stroke="currentColor" strokeWidth="2.2"/>
        <circle cx="58" cy="34" r="4.5" stroke="currentColor" strokeWidth="2.2"/>
        <path d="M18 14 L22 21 L48 21 L52 14" stroke="currentColor" strokeWidth="1.6"/>
      </svg>
    ),
  },
  {
    key: "Sedan",
    label: "Sedan",
    icon: (
      <svg viewBox="0 0 80 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 28 L16 15 L34 10 L54 15 L70 28" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round"/>
        <rect x="4" y="28" width="70" height="6" rx="1.5" stroke="currentColor" strokeWidth="2.2"/>
        <circle cx="18" cy="34" r="4.5" stroke="currentColor" strokeWidth="2.2"/>
        <circle cx="60" cy="34" r="4.5" stroke="currentColor" strokeWidth="2.2"/>
        <path d="M16 15 L22 21 L54 21 L56 15" stroke="currentColor" strokeWidth="1.6"/>
      </svg>
    ),
  },
  {
    key: "SUV",
    label: "SUV",
    icon: (
      <svg viewBox="0 0 80 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="8" y="14" width="62" height="16" rx="2" stroke="currentColor" strokeWidth="2.2"/>
        <path d="M12 14 L16 5 L58 5 L66 14" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round"/>
        <rect x="8" y="26" width="62" height="6" rx="1" stroke="currentColor" strokeWidth="2"/>
        <circle cx="20" cy="34" r="4.5" stroke="currentColor" strokeWidth="2.2"/>
        <circle cx="58" cy="34" r="4.5" stroke="currentColor" strokeWidth="2.2"/>
      </svg>
    ),
  },
  {
    key: "MUV",
    label: "MUV",
    icon: (
      <svg viewBox="0 0 80 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="5" y="10" width="68" height="20" rx="2" stroke="currentColor" strokeWidth="2.2"/>
        <path d="M9 10 L12 4 L66 4 L70 10" stroke="currentColor" strokeWidth="2.2"/>
        <rect x="5" y="26" width="68" height="6" rx="1" stroke="currentColor" strokeWidth="2"/>
        <circle cx="18" cy="34" r="4.5" stroke="currentColor" strokeWidth="2.2"/>
        <circle cx="60" cy="34" r="4.5" stroke="currentColor" strokeWidth="2.2"/>
        <line x1="30" y1="10" x2="30" y2="26" stroke="currentColor" strokeWidth="1.6"/>
        <line x1="48" y1="10" x2="48" y2="26" stroke="currentColor" strokeWidth="1.6"/>
      </svg>
    ),
  },
];

/* ── Price formatter ── */
const formatPrice = (price) => {
  if (!price) return "N/A";
  const lakh = parseFloat(price) / 100000;
  return `₹${lakh % 1 === 0 ? lakh.toFixed(0) : lakh.toFixed(2)} Lakh`;
};

/* ── KM formatter ── */
const formatKm = (km) => {
  if (!km) return null;
  const n = Number(km);
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K km`;
  return `${n} km`;
};

/* ── Skeleton card ── */
const CarSkeleton = () => (
  <div className="ebt-car-skeleton">
    <div className="ebt-skel-img" />
    <div className="ebt-skel-body">
      <div className="ebt-skel-line w70" />
      <div className="ebt-skel-line w45" />
      <div className="ebt-skel-line w55" />
    </div>
  </div>
);

export default function ExploreByBodyType() {
  const [activeType, setActiveType] = useState(BODY_TYPES[0].key);
  const [cars,       setCars]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const navigate    = useNavigate();
  const { selectedCity } = useCity() || {};

  /* ── Fetch real cars using your searchCarsAdvanced ── */
  const fetchCars = useCallback(async (bodyType) => {
    setLoading(true);
    setCars([]);
    try {
      const params = {
        bodyType,
        per_page: 4,
        page: 1,
      };
      if (selectedCity && selectedCity !== "All Cities") {
        params.location = selectedCity;
      }

      const res = await searchCarsAdvanced(params);
      const list = Array.isArray(res)
        ? res
        : res?.data || res?.cars || [];

      setCars(list.slice(0, 4));
    } catch (err) {
      console.error("ExploreByBodyType fetch error:", err);
      setCars([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCity]);

  useEffect(() => {
    fetchCars(activeType);
  }, [activeType, fetchCars]);

  const handleTabClick = (key) => {
    if (key === activeType) return;
    setActiveType(key);
  };

  const handleCarClick = (car) => {
    navigate(`/car/${car.id}`);
  };

  /* ── Always navigates with bodyType filter — never "all cars" ── */
  const handleViewAll = () => {
    const params = new URLSearchParams();
    params.set("bodyType", activeType);           // ← always set, cars or no cars
    if (selectedCity && selectedCity !== "All Cities") {
      params.set("location", selectedCity);
    }
    navigate(`/buy-cars?${params.toString()}`);
  };

  /* ── Car image URL helper ── */
  const getImageUrl = (car) => {
    const raw = car.images_urls?.[0];
    if (!raw) return null;
    return raw.startsWith("http") ? raw : `http://localhost:3001${raw}`;
  };

  /* ── Active label for button text ── */
  const activeLabel = BODY_TYPES.find((bt) => bt.key === activeType)?.label || activeType;

  return (
    <section className="ebt-section">
      <div className="ebt-inner">

        {/* ── Heading ── */}
        <div className="ebt-heading">
          <p className="ebt-tag">✦ BROWSE BY TYPE</p>
          <h2>Explore by Body Type</h2>
        </div>

        {/* ── Tabs ── */}
        <div className="ebt-tabs-scroll">
          <div className="ebt-tabs">
            {BODY_TYPES.map((bt) => (
              <button
                key={bt.key}
                className={`ebt-tab${activeType === bt.key ? " active" : ""}`}
                onClick={() => handleTabClick(bt.key)}
              >
                <span className="ebt-tab-icon">{bt.icon}</span>
                <span className="ebt-tab-label">{bt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Car grid ── */}
        <div className="ebt-grid">
          {loading
            ? [1, 2, 3, 4].map((i) => <CarSkeleton key={i} />)
            : cars.length === 0
            ? (
              <div className="ebt-empty">
                <span>🚗</span>
                <p>
                  No {activeLabel} cars available
                  {selectedCity && selectedCity !== "All Cities" ? ` in ${selectedCity}` : ""}
                  {" "}right now.
                </p>

              </div>
            )
            : cars.map((car) => {
                const imgUrl = getImageUrl(car);
                return (
                  <div
                    key={car.id}
                    className="ebt-car-card"
                    onClick={() => handleCarClick(car)}
                  >
                    {/* Image area */}
                    <div className="ebt-car-img-wrap">
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={`${car.brand} ${car.model}`}
                          className="ebt-car-img"
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextElementSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className="ebt-car-img-placeholder"
                        style={{ display: imgUrl ? "none" : "flex" }}
                      >
                        🚗
                      </div>
                      {car.fuel_type && (
                        <span className="ebt-fuel-badge">{car.fuel_type}</span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="ebt-car-info">
                      <p className="ebt-car-name">
                        {car.brand} {car.model}
                      </p>
                      <p className="ebt-car-meta">
                        {car.year && <span>{car.year}</span>}
                        {car.km_driven && (
                          <>
                            <span className="ebt-sep">·</span>
                            <span>{formatKm(car.km_driven)}</span>
                          </>
                        )}
                        {car.transmission && (
                          <>
                            <span className="ebt-sep">·</span>
                            <span>{car.transmission}</span>
                          </>
                        )}
                      </p>
                      <p className="ebt-car-price">
                        {formatPrice(car.price)}
                        <span className="ebt-onwards"> onwards</span>
                      </p>
                    </div>
                  </div>
                );
              })}
        </div>

        {/* ── View all footer — always shows, cars or no cars (when not loading) ── */}
        {!loading && (
          <div className="ebt-footer">
            <button className="ebt-view-all" onClick={handleViewAll}>
              View all {activeLabel}s →
            </button>
          </div>
        )}

      </div>
    </section>
  );
}